import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Clock, Play, Square, MapPin, Users, Calendar, FileText } from 'lucide-react';
import Layout from '@/components/Layout';
import AdminDashboard from '@/pages/AdminDashboard';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user, onLogout }) {
  // Stjórnendur fá rekstraryfirlit - starfsmenn halda sínu persónulega yfirliti
  if (user.role === 'admin') {
    return <AdminDashboard user={user} onLogout={onLogout} />;
  }
  return <EmployeeDashboard user={user} onLogout={onLogout} />;
}

function EmployeeDashboard({ user, onLogout }) {
  const [activeEntry, setActiveEntry] = useState(null);
  const [location, setLocation] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [stats, setStats] = useState({
    totalHours: 0,
    pendingLeave: 0,
    sickDays: 0,
    vacationDays: 0,
  });

  const fetchActiveEntry = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/time/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.active) {
        setActiveEntry(response.data.entry);
      }
    } catch (error) {
      console.error('Error fetching active entry:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [entriesRes, leaveRes] = await Promise.all([
        axios.get(`${API}/time/entries`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/leave/requests`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const entries = entriesRes.data;
      const totalHours = entries.reduce(
        (sum, entry) => sum + (entry.total_hours || 0),
        0,
      );
      const pendingLeave = leaveRes.data.filter((req) => req.status === 'pending').length;

      let sickDays = 0,
        vacationDays = 0;
      try {
        const sickRes = await axios.get(`${API}/sick-leave/my-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        sickDays = sickRes.data.remaining ?? 0;
        vacationDays = sickRes.data.vacation_days_accrued ?? 0;
      } catch { /* notandi án starfsmannaskráningar */ }

      setStats({
        totalHours: totalHours.toFixed(1),
        pendingLeave,
        sickDays,
        vacationDays,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchActiveEntry();
    fetchStats();
  }, [fetchActiveEntry, fetchStats]);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          toast.success('Staðsetning fundin');
        },
        () => {
          toast.error('Gat ekki fundið staðsetningu');
        },
      );
    } else {
      toast.error('Staðsetning ekki studd');
    }
  };

  const handleClockIn = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/time/clock-in`,
        { location_lat: location?.lat, location_lng: location?.lng },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Stimpluð inn!');
      fetchActiveEntry();
    } catch (error) {
      console.error('Clock in error:', error);
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Villa kom upp');
      }
    }
  };

  const handleClockOut = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/time/clock-out`,
        { entry_id: activeEntry.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Stimpluð út!');
      setActiveEntry(null);
      fetchStats();
    } catch (error) {
      console.error('Clock out error:', error);
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Villa kom upp');
      }
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8" data-testid="dashboard">
        <div>
          <h1
            className="text-4xl font-bold mb-2"
            style={{ fontFamily: 'Work Sans' }}
          >
            Yfirlit
          </h1>
          <p className="text-gray-600">Velkomin/n, {user.full_name}!</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Heildarstundir</CardTitle>
              <Clock className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-700">
                {stats.totalHours}h
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Orlofsdagar</CardTitle>
              <Calendar className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-700">
                {stats.vacationDays}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Veikindadagar eftir</CardTitle>
              <FileText className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-700">
                {stats.sickDays}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Bíðandi beiðnir</CardTitle>
              <Users className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-700">
                {stats.pendingLeave}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clock In/Out Card */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle
              className="text-2xl"
              style={{ fontFamily: 'Work Sans' }}
            >
              Stimpilklukka
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeEntry ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-green-700 font-medium">
                    Stimpluð inn kl.{' '}
                    {new Date(activeEntry.clock_in).toLocaleTimeString('is-IS')}
                  </p>
                </div>
                <Button
                  onClick={handleClockOut}
                  className="w-full bg-red-500 hover:bg-red-600 text-white"
                  size="lg"
                  data-testid="clock-out-button"
                >
                  <Square className="mr-2 h-5 w-5" />
                  Stimpla út
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Button
                    onClick={getLocation}
                    variant="outline"
                    className="flex-1"
                    data-testid="get-location-button"
                  >
                    <MapPin className="mr-2 h-4 w-4" />
                    Fá staðsetningu
                  </Button>
                  {location && (
                    <Button
                      onClick={() => setShowMap(!showMap)}
                      variant="outline"
                      data-testid="toggle-map-button"
                    >
                      {showMap ? 'Fela kort' : 'Sýna kort'}
                    </Button>
                  )}
                </div>

                {location && (
                  <div className="text-sm text-gray-600">
                    Staðsetning: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
                  </div>
                )}

                {showMap && location && (
                  <div
                    className="h-64 rounded-lg overflow-hidden border"
                    data-testid="map-container"
                  >
                    <MapContainer
                      center={[location.lat, location.lng]}
                      zoom={15}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      />
                      <Marker position={[location.lat, location.lng]}>
                        <Popup>Þín staðsetning</Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                )}

                <Button
                  onClick={handleClockIn}
                  className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white"
                  size="lg"
                  data-testid="clock-in-button"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Stimpla inn
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
